import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MRT_ColumnFiltersState, MRT_DensityState, MRT_PaginationState, MRT_SortingState, MRT_TableState, MRT_VisibilityState } from 'material-react-table';
import { queryClient } from '@/lib/queryClient';
import { createUserQueryOptions } from '@/services/queries/pingOptions';
import { createUserSettingsOptions } from '@/hooks/fetches/useSettings.service';
import { Filter } from './interface';

interface ICustomFilters {
  customer: {
    id: number,
    label: string
  } | undefined,
  category: {
    id: number,
    label: 'Active' | 'Closed' | 'All' | 'Assigned' | undefined
  } | undefined,
}

// Define the IReportTableStore interface
interface IReportTableStore {
  showDrawer: boolean;
  pagination: MRT_PaginationState;
  sorting: MRT_SortingState;
  columnFilters: MRT_ColumnFiltersState;
  columnVisibility: MRT_VisibilityState;
  density: MRT_DensityState;
  globalFilter: string | undefined;
  showColumnFilters: boolean;
  customFilters: ICustomFilters
  ticketIds: number[]
  isFilterVisible: boolean
  columnOrder: string[]
  isReportsFiltered: boolean
  reportFilter: Filter
}

// Define the Zustand store interface
interface ReportTableStore {
  reportTableState: IReportTableStore;
  updateReportTable: (newState: Partial<IReportTableStore>) => void;
  setPagination: (updater: MRT_PaginationState | ((prev: MRT_PaginationState) => MRT_PaginationState)) => void;
  setSorting: (updater: MRT_SortingState | ((prev: MRT_SortingState) => MRT_SortingState)) => void;
  setColumnFilters: (updater: MRT_ColumnFiltersState | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)) => void;
  setColumnVisibility: (updater: MRT_VisibilityState | ((prev: MRT_VisibilityState) => MRT_VisibilityState)) => void;
  setDensity: (updater: MRT_DensityState | ((prev: MRT_DensityState) => MRT_DensityState)) => void;
  setGlobalFilter: (updater: string | undefined | ((prev: string | undefined) => string | undefined)) => void;
  setShowColumnFilters: (updater: boolean | ((prev: boolean) => boolean)) => void;
  setCustomFilters: (updater: ICustomFilters | ((prev: ICustomFilters) => ICustomFilters)) => void;
  setTicketIds: (updater: number[] | ((prev: number[]) => number[])) => void;
  setIsFilterVisible: (updater: boolean | ((prev: boolean) => boolean)) => void
  setColumnOrder: (updater: string[] | ((prev: string[]) => string[])) => void;
  setIsReportsFiltered: (updater: boolean | ((prev: boolean) => boolean)) => void
  setReportFilter: (updater: Filter | ((prev: Filter) => Filter)) => void
  reset: () => void
}

const defaultColumnOrder = [
  "TId",
  "Status",
  "Type",
  "conditionalColumn",
  "Priority",
  "Title",
  "Assignee",
  "CustomerName",
  "RaisedBy",
  "DueDate",
  "Created",
  "ClientName",
  "ClientEmail",
  "ClientPhone",
  "ClientAddress",
]


// export const getTableColumnSortingState = async (): Promise<any> => {

//   try {
//     const agentByContext = await queryClient.fetchQuery(createUserQueryOptions({ ticketid: 0, customerid: 0, limittocustomer: false }));

//     const getSettings = await queryClient.fetchQuery(createUserSettingsOptions(agentByContext?.ContextUser?.AgentId));

//     const columnSorting = getSettings?.data?.Data?.find(
//       (entry: any) => entry.Identifier === 'reportTableColumnSorting'
//     )?.ColumnOrder;

//     return columnSorting || defaultColumnOrder
//   }
//   catch (error) {
//     console.log(error)
//     return defaultColumnOrder
//   }
// }

const initialTableState: IReportTableStore = {
  showDrawer: false,
  pagination: { pageIndex: 0, pageSize: 25 },
  sorting: [{ id: 'TId', desc: true }],
  columnFilters: [],
  columnVisibility: { conditionalColumn: false },
  density: 'compact',
  globalFilter: undefined,
  showColumnFilters: false,
  customFilters: { customer: undefined, category: undefined },
  ticketIds: [],
  isFilterVisible: true,
  columnOrder: defaultColumnOrder,
  isReportsFiltered: false,
  reportFilter: { id: '', name: '', toggleOn: false, condition: [] },
}

// Create the Zustand store with persistence
const useReportTableStore = create<ReportTableStore>()(
  persist(
    (set) => ({
      reportTableState: initialTableState,
      updateReportTable: (newState: Partial<IReportTableStore>) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            ...newState,
          },
        })),
      setPagination: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            pagination: typeof updater === 'function' ? updater(state.reportTableState.pagination) : updater,
          }
        })),


      setSorting: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            sorting: typeof updater === 'function' ? updater(state.reportTableState.sorting) : updater,
          }
        })),

      setColumnFilters: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            columnFilters: typeof updater === 'function' ? updater(state.reportTableState.columnFilters) : updater,
          }
        })),

      setColumnVisibility: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            columnVisibility: typeof updater === 'function' ? updater(state.reportTableState.columnVisibility) : updater,
          }
        })),

      setDensity: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            density: typeof updater === 'function' ? updater(state.reportTableState.density) : updater,
          }
        })),
      setGlobalFilter: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            globalFilter: typeof updater === 'function' ? updater(state.reportTableState.globalFilter) : updater,
          }
        })),
      setShowColumnFilters: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            showColumnFilters: typeof updater === 'function' ? updater(state.reportTableState.showColumnFilters) : updater,
          }
        })),
      setCustomFilters: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            customFilters: typeof updater === 'function' ? updater(state.reportTableState.customFilters) : updater,
          }
        })),
      setTicketIds: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            ticketIds: typeof updater === 'function' ? updater(state.reportTableState.ticketIds) : updater,
          }
        })),
      setIsFilterVisible: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            isFilterVisible: typeof updater === 'function' ? updater(state.reportTableState.isFilterVisible) : updater,
          }
        })),
      setColumnOrder: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            columnOrder: typeof updater === 'function' ? updater(state.reportTableState.columnOrder) : updater,
          }
        })),
      setIsReportsFiltered: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            isReportsFiltered: typeof updater === 'function' ? updater(state.reportTableState.isReportsFiltered) : updater,
          }
        })),
      setReportFilter: (updater) =>
        set((state) => ({
          reportTableState: {
            ...state.reportTableState,
            reportFilter: typeof updater === 'function' ? updater(state.reportTableState.reportFilter) : updater,
          }
        })),
      reset: () => set({ reportTableState: initialTableState }),
    }),
    {
      name: 'report-table-storage',
      partialize: (state) => ({
        reportTableState: {
          pagination: state.reportTableState.pagination,
          sorting: state.reportTableState.sorting,
          columnFilters: state.reportTableState.columnFilters,
          columnVisibility: state.reportTableState.columnVisibility,
          density: state.reportTableState.density,
          globalFilter: state.reportTableState.globalFilter,
          showColumnFilters: state.reportTableState.showColumnFilters,
          customFilters: state.reportTableState.customFilters,
          ticketIds: state.reportTableState.ticketIds,
          columnOrder: state.reportTableState.columnOrder,
          isFilterVisible: state.reportTableState.isFilterVisible,
          isReportsFiltered: state.reportTableState.isReportsFiltered,
          reportFilter: state.reportTableState.reportFilter
        },
      }),
    }
  )
);

export default useReportTableStore;